
import React, { useState } from 'react';
import { usePendingApprovals } from '../../hooks/useLeaveRequests';
import { leaveRequestsService } from '../../services/api/leaveRequests';
import StatusBadge from '../../components/common/StatusBadge';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import './LeaveApprovals.css';

const LeaveApprovals: React.FC = () => {
  const { requests, loading, error, refetch } = usePendingApprovals();
  const { showSuccess, showError, showWarning } = useToast();
  const [selectedRequest, setSelectedRequest] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveConfirm, setApproveConfirm] = useState<{ isOpen: boolean; requestId: number | null }>({
    isOpen: false,
    requestId: null,
  });

  const openApproveConfirm = (id: number) => {
    setApproveConfirm({ isOpen: true, requestId: id });
  };

  const closeApproveConfirm = () => {
    setApproveConfirm({ isOpen: false, requestId: null });
  };

  const handleApprove = async () => {
    const id = approveConfirm.requestId;
    if (!id) return;

    try {
      setProcessing(true);
      console.log('Attempting to approve leave request ID:', id);
      const result = await leaveRequestsService.approve(id);
      console.log('Approval successful:', result);
      showSuccess('Leave request approved successfully!');
      refetch();
      setSelectedRequest(null);
      closeApproveConfirm();
    } catch (err: any) {
      console.error('Approval error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to approve request';
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: errorMessage,
      });
      showError(errorMessage);
      closeApproveConfirm();
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (id: number) => {
    if (!rejectReason.trim()) {
      showWarning('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      await leaveRequestsService.reject(id, { reason: rejectReason });
      showSuccess('Leave request rejected successfully');
      refetch();
      setSelectedRequest(null);
      setRejectReason('');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <Loading text="Loading pending approvals..." />;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;

  return (
    <div className="leave-approvals-page">
      <h1>Pending Approvals</h1>
      <p className="page-subtitle">
        You have {requests.length} leave request(s) pending your approval
      </p>

      {requests.length === 0 ? (
        <div className="empty-state">
          <p>No pending approvals at the moment.</p>
        </div>
      ) : (
        <div className="approvals-grid">
          {requests.map((request) => (
            <div key={request.id} className="approval-card">
              <div className="approval-header">
                <div className="employee-info">
                  <h3>{request.employee?.fullName}</h3>
                  <span className="employee-code">{request.employee?.employeeCode}</span>
                </div>
                <StatusBadge status={request.status} />
              </div>

              <div className="approval-details">
                <div className="detail-row">
                  <span className="label">Leave Type:</span>
                  <span className="value">{request.leaveType?.name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Period:</span>
                  <span className="value">
                    {new Date(request.startDate).toLocaleDateString('en-GB')} -
                    {new Date(request.endDate).toLocaleDateString('en-GB')}
                    ({request.totalDays} days)
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted:</span>
                  <span className="value">
                    {new Date(request.submittedAt!).toLocaleString('en-GB')}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Reason:</span>
                  <span className="value">{request.reason}</span>
                </div>
                {(request.isLateSubmission === true || request.isLateSubmission === 1) && (
                  <div className="warning-box">
                    ⚠️ This is a late submission
                  </div>
                )}
              </div>

              <div className="approval-actions">
                {selectedRequest === request.id ? (
                  <div className="reject-form">
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Please provide a reason for rejection..."
                      rows={3}
                      className="reject-textarea"
                    />
                    <div className="reject-actions">
                      <button
                        onClick={() => {
                          setSelectedRequest(null);
                          setRejectReason('');
                        }}
                        className="button button-secondary"
                        disabled={processing}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleReject(request.id)}
                        className="button button-danger"
                        disabled={processing}
                      >
                        {processing ? 'Rejecting...' : 'Confirm Rejection'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setSelectedRequest(request.id)}
                      className="button button-danger-outline"
                      disabled={processing}
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => openApproveConfirm(request.id)}
                      className="button button-success"
                      disabled={processing}
                    >
                      Approve
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={approveConfirm.isOpen}
        onClose={closeApproveConfirm}
        onConfirm={handleApprove}
        title="Approve Leave Request"
        message="Are you sure you want to approve this leave request? This action cannot be undone."
        confirmText="Approve"
        cancelText="Cancel"
        variant="success"
        isLoading={processing}
      />
    </div>
  );
};

export default LeaveApprovals;